import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, Save, CheckCircle, AlertCircle,
  Clock, User, FileText, Award, ClipboardPen, Loader2, Edit, Trash2
} from 'lucide-react';
import {
  getSessionReview,
  triggerAIGrading,
  updateFinalGrade,
  finalizeGrading,
  getSessionEvents
} from '../lib/examApi';
import { useToast, useConfirm } from './ui';
import type { SessionWithAnswers, AnswerWithQuestion } from '../types/exam';

const ExamReview: React.FC = () => {
  const toast = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();

  const [session, setSession] = useState<SessionWithAnswers | null>(null);
  const [answers, setAnswers] = useState<AnswerWithQuestion[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gradingAI, setGradingAI] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  // Editing state (for final grading)
  const [editingAnswerId, setEditingAnswerId] = useState<string | null>(null);
  const [editScore, setEditScore] = useState<number>(0);
  const [editFeedback, setEditFeedback] = useState<string>('');

  // Editing state (for AI grading)
  const [editingAIAnswerId, setEditingAIAnswerId] = useState<string | null>(null);
  const [editAIScore, setEditAIScore] = useState<number>(0);
  const [editAIFeedback, setEditAIFeedback] = useState<string>('');

  useEffect(() => {
    if (sessionId) {
      loadSessionReview();
    }
  }, [sessionId]);

  const loadSessionReview = async () => {
    if (!sessionId) return;

    try {
      setLoading(true);
      const data = await getSessionReview(sessionId);
      setSession(data.session);
      setAnswers(data.answers);

      // Load events
      try {
        const eventData = await getSessionEvents(sessionId);
        setEvents(eventData);
      } catch (evtErr) {
        console.error('Failed to load events:', evtErr);
        // Don't fail the whole page if events fail to load
      }

      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerAIGrading = async () => {
    if (!sessionId) return;

    const confirmed = await confirm({
      title: 'KI-gradering',
      message: 'Trigger KI-gradering for alle skriftlige svar? Dette vil overskrive eksisterende KI-graderinger.',
      confirmLabel: 'Start gradering',
      cancelLabel: 'Avbryt',
      variant: 'warning'
    });

    if (!confirmed) return;

    try {
      setGradingAI(true);
      setError(null);
      const result = await triggerAIGrading(sessionId);
      toast.showSuccess(`KI-gradering fullført for ${result.graded_count} svar`);
      await loadSessionReview();
    } catch (err: any) {
      setError('KI-gradering feilet: ' + err.message);
    } finally {
      setGradingAI(false);
    }
  };

  const startEditingAnswer = (answer: AnswerWithQuestion) => {
    setEditingAnswerId(answer.id);
    setEditScore(answer.final_score ?? answer.ai_grading_score ?? 0);
    setEditFeedback(answer.final_feedback ?? answer.ai_grading_feedback ?? '');
  };

  const cancelEditing = () => {
    setEditingAnswerId(null);
    setEditScore(0);
    setEditFeedback('');
  };

  const startEditingAI = (answer: AnswerWithQuestion) => {
    setEditingAIAnswerId(answer.id);
    setEditAIScore(answer.ai_grading_score ?? 0);
    setEditAIFeedback(answer.ai_grading_feedback ?? '');
  };

  const cancelEditingAI = () => {
    setEditingAIAnswerId(null);
    setEditAIScore(0);
    setEditAIFeedback('');
  };

  const saveAIGrade = async (answerId: string, maxPoints: number) => {
    if (editAIScore < 0 || editAIScore > maxPoints) {
      toast.showWarning(`Poeng må være mellom 0 og ${maxPoints}`);
      return;
    }

    try {
      // Call backend API to update AI grading
      const response = await fetch(`/api/exams/answers/${answerId}/ai-grade`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          ai_grading_score: editAIScore,
          ai_grading_feedback: editAIFeedback.trim() || undefined
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update AI grading');
      }

      // Update local state
      setAnswers(answers.map(a =>
        a.id === answerId
          ? { ...a, ai_grading_score: editAIScore, ai_grading_feedback: editAIFeedback.trim() || undefined, ai_graded_at: new Date().toISOString() }
          : a
      ));

      cancelEditingAI();
    } catch (err: any) {
      toast.showError('Kunne ikke lagre AI-vurdering: ' + err.message);
    }
  };

  const deleteAIGrade = async (answerId: string) => {
    const confirmed = await confirm({
      title: 'Slett KI-vurdering',
      message: 'Er du sikker på at du vil slette KI-vurderingen?',
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const response = await fetch(`/api/exams/answers/${answerId}/ai-grade`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete AI grading');
      }

      setAnswers(answers.map(a =>
        a.id === answerId
          ? { ...a, ai_grading_score: undefined, ai_grading_feedback: undefined, ai_graded_at: undefined }
          : a
      ));
    } catch (err: any) {
      toast.showError('Kunne ikke slette AI-vurdering: ' + err.message);
    }
  };

  const deleteFinalGrade = async (answerId: string) => {
    const confirmed = await confirm({
      title: 'Slett vurdering',
      message: 'Er du sikker på at du vil slette endelig vurdering?',
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;

    try {
      const response = await fetch(`/api/exams/answers/${answerId}/final-grade`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete final grading');
      }

      setAnswers(answers.map(a =>
        a.id === answerId
          ? { ...a, final_score: undefined, final_feedback: undefined, graded_at: undefined }
          : a
      ));
    } catch (err: any) {
      toast.showError('Kunne ikke slette endelig vurdering: ' + err.message);
    }
  };

  const saveGrade = async (answerId: string, maxPoints: number) => {
    if (editScore < 0 || editScore > maxPoints) {
      toast.showWarning(`Poeng må være mellom 0 og ${maxPoints}`);
      return;
    }

    try {
      await updateFinalGrade(answerId, {
        final_score: editScore,
        final_feedback: editFeedback.trim() || undefined
      });

      // Update local state
      setAnswers(answers.map(a =>
        a.id === answerId
          ? { ...a, final_score: editScore, final_feedback: editFeedback.trim() || undefined, graded_at: new Date().toISOString() }
          : a
      ));

      cancelEditing();
    } catch (err: any) {
      toast.showError('Kunne ikke lagre karakteren: ' + err.message);
    }
  };

  const handleFinalize = async () => {
    if (!sessionId) return;

    // Check if all answers have been graded
    const ungradedAnswers = answers.filter(a =>
      a.final_score === null && a.ai_grading_score === null && a.question_type === 'written'
    );

    if (ungradedAnswers.length > 0) {
      const continueConfirmed = await confirm({
        title: 'Ugraderte svar',
        message: `Det er ${ungradedAnswers.length} skriftlige svar som ikke er gradert. Vil du fortsette likevel? Disse vil få 0 poeng.`,
        confirmLabel: 'Fortsett',
        cancelLabel: 'Avbryt',
        variant: 'warning'
      });
      if (!continueConfirmed) return;
    }

    const finalConfirmed = await confirm({
      title: 'Fullfør karaktersetting',
      message: 'Fullfør karaktersetting og publiser resultat? Dette kan ikke angres.',
      confirmLabel: 'Fullfør',
      cancelLabel: 'Avbryt',
      variant: 'warning'
    });

    if (!finalConfirmed) return;

    try {
      setFinalizing(true);
      setError(null);
      const result = await finalizeGrading(sessionId);
      toast.showSuccess(
        `Karaktersetting fullført! Poeng: ${Number(result.total_points)}/${Number(result.max_points)} (${result.percentage}%), Karakter: ${result.grade}, ${result.passed ? 'Bestått' : 'Ikke bestått'}`
      );
      navigate(`/exams/${session?.exam_set_id}`);
    } catch (err: any) {
      setError('Kunne ikke fullføre karaktersetting: ' + err.message);
    } finally {
      setFinalizing(false);
    }
  };

  const getQuestionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      multiple_choice: 'Flervalg',
      written: 'Skriftlig',
      true_false: 'Sant/Usant'
    };
    return labels[type] || type;
  };

  const getGradingScaleLabel = (scale: string) => {
    const labels: Record<string, string> = {
      pass_fail: 'Bestått/Ikke bestått',
      three_tier: 'Meget godt bestått/Bestått/Ikke bestått',
      a_f: 'A-F',
      percentage: 'Prosent'
    };
    return labels[scale] || scale;
  };

  const getGradingScaleInfo = () => {
    if (!session) return null;

    const passThreshold = session.pass_threshold;
    const gradingScale = session.grading_scale;

    if (gradingScale === 'pass_fail') {
      return `Bestått: ${passThreshold}% eller høyere`;
    } else if (gradingScale === 'three_tier') {
      return `Meget godt bestått: 90%+, Bestått: ${passThreshold}%+, Ikke bestått: <${passThreshold}%`;
    } else if (gradingScale === 'a_f') {
      const range = 90 - passThreshold;
      const gradeWidth = range / 4;
      const bThreshold = Math.round(90 - gradeWidth);
      const cThreshold = Math.round(90 - (gradeWidth * 2));
      const dThreshold = Math.round(90 - (gradeWidth * 3));

      return `A: 90-100%, B: ${bThreshold}-89%, C: ${cThreshold}-${bThreshold-1}%, D: ${dThreshold}-${cThreshold-1}%, E: ${passThreshold}-${dThreshold-1}%, F: <${passThreshold}% (ikke bestått)`;
    } else if (gradingScale === 'percentage') {
      return `Bestått: ${passThreshold}% eller høyere`;
    }

    return null;
  };

  const calculateCurrentTotal = () => {
    return answers.reduce((sum, a) => {
      const score = a.final_score ?? a.ai_grading_score ?? 0;
      return sum + Number(score);
    }, 0);
  };

  const calculateMaxTotal = () => {
    return answers.reduce((sum, a) => sum + Number(a.max_points), 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Laster prøvesvar...</div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">Feil ved lasting</h3>
          <p className="text-red-600 mt-1">{error || 'Kunne ikke laste prøvesvar'}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-2 text-sm text-red-700 underline"
          >
            Gå tilbake
          </button>
        </div>
      </div>
    );
  }

  const currentTotal = calculateCurrentTotal();
  const maxTotal = calculateMaxTotal();
  const currentPercentage = maxTotal > 0 ? Math.round((currentTotal / maxTotal) * 100) : 0;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">Vurdering av prøvesvar</h1>
          <p className="text-gray-600 mt-1">{session.exam_title}</p>
        </div>
      </div>

      {/* Session Info */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="flex items-start gap-3">
            <User className="text-blue-600 mt-1" size={20} />
            <div>
              <div className="text-sm text-gray-600">Kandidat</div>
              <div className="font-medium text-gray-900">{session.anonymous_id}</div>
              <div className="text-xs text-gray-500 mt-1">{session.unique_candidate_id}</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Clock className="text-blue-600 mt-1" size={20} />
            <div>
              <div className="text-sm text-gray-600">Innlevert</div>
              <div className="font-medium text-gray-900">
                {session.submitted_at
                  ? new Date(session.submitted_at).toLocaleString('nb-NO')
                  : 'Ikke innlevert'}
              </div>
            </div>
          </div>
          {session.graded_at && (
            <div className="flex items-start gap-3">
              <CheckCircle className="text-green-600 mt-1" size={20} />
              <div>
                <div className="text-sm text-gray-600">Sensurert</div>
                <div className="font-medium text-gray-900">
                  {new Date(session.graded_at).toLocaleString('nb-NO')}
                </div>
              </div>
            </div>
          )}
          <div className="flex items-start gap-3">
            <Award className="text-blue-600 mt-1" size={20} />
            <div>
              <div className="text-sm text-gray-600">Grense for bestått</div>
              <div className="font-medium text-gray-900">{session.pass_threshold}%</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <FileText className="text-blue-600 mt-1" size={20} />
            <div>
              <div className="text-sm text-gray-600">Karakterskala</div>
              <div className="font-medium text-gray-900">{getGradingScaleLabel(session.grading_scale)}</div>
            </div>
          </div>
        </div>

        {/* Grading Scale Info */}
        {getGradingScaleInfo() && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Award className="text-blue-600 mt-0.5" size={18} />
              <div>
                <div className="text-sm font-semibold text-blue-900 mb-1">Karakterskala for denne prøven</div>
                <div className="text-sm text-blue-800">{getGradingScaleInfo()}</div>
              </div>
            </div>
          </div>
        )}

        {/* Current Score */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Foreløpig resultat</div>
              <div className="text-2xl font-bold text-gray-900">
                {currentTotal} / {maxTotal} poeng
              </div>
              <div className="text-lg text-gray-700">
                {currentPercentage}%
                {currentPercentage >= session.pass_threshold ? (
                  <span className="ml-2 text-green-600">(Bestått)</span>
                ) : (
                  <span className="ml-2 text-red-600">(Ikke bestått)</span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {session.status === 'submitted' && (
                <button
                  onClick={handleTriggerAIGrading}
                  disabled={gradingAI}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition"
                >
                  {gradingAI ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <Sparkles size={20} />
                  )}
                  {gradingAI ? 'Graderer...' : 'KI-gradering'}
                </button>
              )}
              {session.status !== 'graded' && (
                <button
                  onClick={handleFinalize}
                  disabled={finalizing}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
                >
                  {finalizing ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <CheckCircle size={20} />
                  )}
                  {finalizing ? 'Fullfører...' : 'Fullfør karaktersetting'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Message */}
      {session.status === 'graded' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle size={20} />
            <span className="font-medium">Denne prøven er allerede karaktersatt og resultatet er publisert.</span>
          </div>
        </div>
      )}

      {session.status === 'in_progress' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-yellow-800">
            <AlertCircle size={20} />
            <span className="font-medium">Kandidaten har ikke levert prøven ennå.</span>
          </div>
        </div>
      )}

      {/* Answers */}
      <div className="space-y-4">
        {answers.map((answer, index) => {
          const isEditing = editingAnswerId === answer.id;
          const hasAIGrading = answer.ai_grading_score != null;
          const hasFinalGrading = answer.final_score != null;
          const displayScore = Number(answer.final_score ?? answer.ai_grading_score ?? 0);

          return (
            <div key={answer.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Question Header */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm font-medium text-gray-600">
                        Spørsmål {index + 1}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                        {getQuestionTypeLabel(answer.question_type)}
                      </span>
                      <span className="text-sm text-gray-600">
                        {Number(answer.max_points)} {Number(answer.max_points) === 1 ? 'poeng' : 'poeng'}
                      </span>
                    </div>
                    <p className="text-gray-900 font-medium">{answer.question_text}</p>
                  </div>
                  <div className="ml-4 text-right">
                    <div className="text-sm text-gray-600">Poeng</div>
                    <div className={`text-2xl font-bold ${
                      displayScore === Number(answer.max_points) ? 'text-green-600' :
                      displayScore > 0 ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {displayScore}
                    </div>
                  </div>
                </div>
              </div>

              {/* Answer Content */}
              <div className="px-6 py-4">
                {/* Multiple Choice / True-False */}
                {(answer.question_type === 'multiple_choice' || answer.question_type === 'true_false') && (
                  <div>
                    <div className="text-sm text-gray-600 mb-2">Kandidatens svar:</div>
                    <div className="space-y-2">
                      {answer.options?.map((opt, i) => {
                        const isSelected = answer.selected_option === i;
                        const isCorrect = opt.is_correct;

                        return (
                          <div
                            key={i}
                            className={`p-3 rounded border ${
                              isSelected && isCorrect
                                ? 'bg-green-50 border-green-300'
                                : isSelected && !isCorrect
                                ? 'bg-red-50 border-red-300'
                                : isCorrect
                                ? 'bg-green-50 border-green-200'
                                : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{opt.text}</span>
                              {isSelected && (
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  Valgt
                                </span>
                              )}
                              {isCorrect && (
                                <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800">
                                  Riktig svar
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3 text-sm">
                      {answer.selected_option !== null && answer.selected_option !== undefined && answer.options ? (
                        answer.options[answer.selected_option]?.is_correct ? (
                          <span className="text-green-600 font-medium">✓ Riktig svar</span>
                        ) : (
                          <span className="text-red-600 font-medium">✗ Feil svar</span>
                        )
                      ) : (
                        <span className="text-gray-500">Ingen svar</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Written Answer */}
                {answer.question_type === 'written' && (
                  <div>
                    <div className="text-sm text-gray-600 mb-2">Kandidatens svar:</div>
                    <div className="bg-gray-50 rounded p-4 mb-4">
                      <p className="text-gray-900 whitespace-pre-wrap">
                        {answer.answer_text || '(Ingen svar)'}
                      </p>
                    </div>

                    {answer.grading_criteria && (
                      <div className="mb-4">
                        <div className="text-sm text-gray-600 mb-2">Vurderingskriterier:</div>
                        <div className="bg-blue-50 rounded p-3">
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">
                            {answer.grading_criteria}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* AI Grading */}
                    {hasAIGrading && editingAIAnswerId !== answer.id && (
                      <div className="mb-4 bg-purple-50 rounded p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <ClipboardPen size={16} className="text-purple-600" />
                            <span className="text-sm font-medium text-purple-900">Skriftlig vurdering</span>
                          </div>
                          {session.status !== 'graded' && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => startEditingAI(answer)}
                                className="text-purple-600 hover:text-purple-800"
                                title="Rediger KI-vurdering"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => deleteAIGrade(answer.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Slett KI-vurdering"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-gray-800 mb-2">
                          <span className="font-medium">Poeng:</span> {Number(answer.ai_grading_score)} / {Number(answer.max_points)}
                        </div>
                        {answer.ai_grading_feedback && (
                          <div className="text-sm text-gray-700">
                            <span className="font-medium">Tilbakemelding:</span>
                            <p className="mt-1 whitespace-pre-wrap">{answer.ai_grading_feedback}</p>
                          </div>
                        )}
                        {answer.ai_graded_at && (
                          <div className="text-xs text-gray-500 mt-2">
                            Vurdert: {new Date(answer.ai_graded_at).toLocaleString('nb-NO')}
                          </div>
                        )}
                      </div>
                    )}

                    {/* AI Grading Edit Form */}
                    {editingAIAnswerId === answer.id && (
                      <div className="mb-4 bg-purple-50 rounded p-4">
                        <div className="text-sm font-medium text-gray-900 mb-3">Rediger KI-vurdering</div>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">
                              Poeng (0-{Number(answer.max_points)})
                            </label>
                            <input
                              type="number"
                              min="0"
                              max={Number(answer.max_points)}
                              step="0.5"
                              value={editAIScore}
                              onChange={(e) => setEditAIScore(parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">
                              Tilbakemelding
                            </label>
                            <textarea
                              value={editAIFeedback}
                              onChange={(e) => setEditAIFeedback(e.target.value)}
                              rows={4}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              placeholder="Skriv tilbakemelding..."
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveAIGrade(answer.id, Number(answer.max_points))}
                              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                            >
                              <Save size={16} />
                              Oppdater
                            </button>
                            <button
                              onClick={cancelEditingAI}
                              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
                            >
                              Avbryt
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Final Grading */}
                    {hasFinalGrading && !isEditing && (
                      <div className="mb-4 bg-green-50 rounded p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <CheckCircle size={16} className="text-green-600" />
                            <span className="text-sm font-medium text-green-900">Endelig vurdering</span>
                          </div>
                          {session.status !== 'graded' && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => startEditingAnswer(answer)}
                                className="text-green-600 hover:text-green-800"
                                title="Rediger endelig vurdering"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => deleteFinalGrade(answer.id)}
                                className="text-red-600 hover:text-red-800"
                                title="Slett endelig vurdering"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-gray-800 mb-2">
                          <span className="font-medium">Poeng:</span> {Number(answer.final_score)} / {Number(answer.max_points)}
                        </div>
                        {answer.final_feedback && (
                          <div className="text-sm text-gray-700">
                            <span className="font-medium">Tilbakemelding:</span>
                            <p className="mt-1 whitespace-pre-wrap">{answer.final_feedback}</p>
                          </div>
                        )}
                        {answer.graded_at && (
                          <div className="text-xs text-gray-500 mt-2">
                            Vurdert: {new Date(answer.graded_at).toLocaleString('nb-NO')}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Edit Form */}
                    {isEditing ? (
                      <div className="bg-yellow-50 rounded p-4">
                        <div className="text-sm font-medium text-gray-900 mb-3">Rediger karakter</div>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">
                              Poeng (0-{Number(answer.max_points)})
                            </label>
                            <input
                              type="number"
                              min="0"
                              max={Number(answer.max_points)}
                              step="0.5"
                              value={editScore}
                              onChange={(e) => setEditScore(parseFloat(e.target.value) || 0)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">
                              Tilbakemelding (valgfritt)
                            </label>
                            <textarea
                              value={editFeedback}
                              onChange={(e) => setEditFeedback(e.target.value)}
                              rows={3}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Skriv tilbakemelding til kandidaten..."
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => saveGrade(answer.id, Number(answer.max_points))}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                            >
                              <Save size={16} />
                              Lagre
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
                            >
                              Avbryt
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : session.status !== 'graded' && !hasFinalGrading && (
                      <button
                        onClick={() => startEditingAnswer(answer)}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Legg til vurdering
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom Actions */}
      {session.status !== 'graded' && (
        <div className="mt-6 flex justify-between items-center bg-white rounded-lg border border-gray-200 p-6">
          <div>
            <div className="text-sm text-gray-600">Foreløpig total</div>
            <div className="text-xl font-bold text-gray-900">
              {currentTotal} / {maxTotal} poeng ({currentPercentage}%)
            </div>
          </div>
          <button
            onClick={handleFinalize}
            disabled={finalizing}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition text-lg font-medium"
          >
            {finalizing ? (
              <Loader2 size={24} className="animate-spin" />
            ) : (
              <CheckCircle size={24} />
            )}
            {finalizing ? 'Fullfører...' : 'Fullfør karaktersetting'}
          </button>
        </div>
      )}

      {/* Event Log */}
      {events.length > 0 && (
        <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Hendelseslogg</h3>
          <div className="space-y-2">
            {events.map((event) => (
              <div key={event.id} className="flex items-start gap-3 text-sm border-l-2 border-gray-300 pl-4 py-2">
                <Clock size={16} className="text-gray-500 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{event.event_description}</div>
                  <div className="text-gray-600 text-xs mt-1">
                    {new Date(event.created_at).toLocaleString('nb-NO')}
                    {event.performed_by_user_name && (
                      <span className="ml-2">• {event.performed_by_user_name}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamReview;
